import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { TokenHttp } from '../services/tokenhttp.service';
import { BaseRepository } from './base.repository';
import { DisplayInvoice, ErpInvoice, ErpProduct, InvoiceConsumer, InvoiceOverview, InvoicePlant, InvoiceSpreadMessage, InvoiceTour, SelectableTourGroup, SpreadMessageGroup, TourGroup, CreditorListItem, InvoiceSupplementDto, Seller, InvoiceDto, InvoiceMetaDataDto } from '../interfaces/invoices';
import { InvoiceType } from '../components/invoices/models/InvoiceType';



@Injectable()
export class InvoiceRepository extends BaseRepository {
    constructor(tokenHttp: TokenHttp) {
        super(tokenHttp);
    }

    getOverview(
        invoiceNumber: string | null,
        type: InvoiceType | null,
        fromDate: Date | null,
        toDate: Date | null,
        skip: number,
        take: number
    ): Observable<InvoiceOverview[]> {
        const params = {
            skip,
            take,
            invoiceNumber,
            type,
            fromDate,
            toDate,
        };

        return this.mapGet("invoice/overview", params);
    }

    getConsumers(): Observable<InvoiceConsumer[]> {
        return this.mapGet("invoice/consumers");
    }

    getPlants(): Observable<InvoicePlant[]> {
        return this.mapGet("invoice/plants");
    }

    getErpProducts(): Observable<ErpProduct[]> {
        return this.mapGet("invoice/erp-products");
    }

    getTours(
        plantId: number | null,
        fromDate: Date | null,
        toDate: Date | null
    ): Observable<TourGroup[]> {
        const params = {
            plantId,
            fromDate,
            toDate
        }

        return this.mapGet("invoice/tours", params);
    }

    anyToursNotInvoicedBeforeDate(
        plantId: number | null,
        date: Date | null
    ): Observable<InvoiceMetaDataDto> {
        const params = {
            plantId,
            date
        }

        return this.mapGet("invoice/anyToursNotInvoicedBeforeDate", params);
    }

    getSpreadMessages(
        consumerId: number | null,
        sellerName: string | null,
        fromDate: Date | null,
        toDate: Date | null
    ): Observable<SpreadMessageGroup[]> {
        const params = {
            consumerId,
            sellerName,
            fromDate,
            toDate
        }

        return this.mapGet("invoice/spreadmessages", params);
    }


    createPurchaseInvoice(invoice: ErpInvoice<InvoiceSpreadMessage>) {
        return this.mapPost("invoice/create/purchase", invoice);
    }
    createSalesInvoice(invoice: ErpInvoice<InvoiceTour>) {
        return this.mapPost("invoice/create/sales", invoice)
    }

    getInvoice(invoiceId: string): Observable<DisplayInvoice> {
        return this.mapGet(`invoice/${invoiceId}`);
    }

    cancelInvoice(invoiceId: string): Observable<object> {
        return this.mapPost(`invoice/cancel/${invoiceId}`);
    }

    getSellers(): Observable<Seller[]> {
        return this.mapGet("invoice/sellers");
    }

    getCreditors(): Observable<CreditorListItem[]> {
        return this.mapGet("invoice/getcreditors");
    }

    getSupplementsByType(entityType: string, entityId: number): Observable<InvoiceSupplementDto[]> {
        return this.mapGet(`invoice/getsupplements?entityType=${entityType}&entityId=${entityId}`);
    }

    saveSupplement(dto: InvoiceSupplementDto): Observable<InvoiceSupplementDto> {
        return this.mapPost(`invoice/createsupplement`, dto)
    }

    updateSupplement(dto: InvoiceSupplementDto): Observable<InvoiceSupplementDto> {
        return this.mapPost(`invoice/updatesupplement`, dto)
    }

    updateErpDeptorNumber(invoice: InvoiceDto): Observable<InvoiceDto> {
        return this.mapPut(`invoice/${invoice.id}/updateErpDeptorNumber`, invoice);
    }

    deleteSupplement(id: string) {
        return this.mapDelete(`invoice/deletesupplement/${id}`)

    }
    validateVendorNumber(vendorNumber: string) {
        return this.mapGet(`invoice/validate/vendor/${vendorNumber}`);
    }

    validateCustomerNumber(customerNumber: string) {
        return this.mapGet(`invoice/validate/customer/${customerNumber}`);
    }

    updatePlantNumber(plant: { plantId: number, erpCustomerNumber: string, newErpCustomerNumber: string }) {
        return this.mapPost("invoice/utils/erp/update-plant", plant);
    }

    resendToBc(invoiceId: string): Observable<{invoiceNumber: string}> {
        return this.mapPost(`invoice/resend-to-bc/${invoiceId}`);
    }
}
